/**
 * Deck Management Utilities for Teen Patti
 *
 * Standard 52-card deck (no jokers) with Fisher-Yates shuffle.
 * Deals 3 cards per player.
 */

import { Card, Deck, SUITS, RANKS } from './types';

/**
 * Create a standard 52-card deck (4 suits × 13 ranks).
 */
export function createDeck(): Deck {
  const cards: Card[] = [];
  for (const suit of SUITS) {
    for (const rank of RANKS) {
      cards.push({ suit, rank });
    }
  }
  return { cards, dealtCards: [] };
}

/**
 * Shuffle the deck using Fisher-Yates algorithm.
 * Returns a new Deck with shuffled cards.
 */
export function shuffleDeck(deck: Deck): Deck {
  const cards = [...deck.cards];
  for (let i = cards.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [cards[i], cards[j]] = [cards[j], cards[i]];
  }
  return { ...deck, cards };
}

/**
 * Deal one card from the top of the deck.
 */
export function dealCard(deck: Deck): { card: Card; deck: Deck } {
  if (deck.cards.length === 0) {
    throw new Error('Cannot deal from an empty deck');
  }
  const [card, ...remaining] = deck.cards;
  return {
    card,
    deck: {
      ...deck,
      cards: remaining,
      dealtCards: [...deck.dealtCards, card],
    },
  };
}

/**
 * Deal a specified number of cards from the deck.
 */
export function dealCards(deck: Deck, count: number): { cards: Card[]; deck: Deck } {
  if (deck.cards.length < count) {
    throw new Error(`Not enough cards. Need ${count}, have ${deck.cards.length}.`);
  }
  const dealt = deck.cards.slice(0, count);
  const remaining = deck.cards.slice(count);
  return {
    cards: dealt,
    deck: {
      ...deck,
      cards: remaining,
      dealtCards: [...deck.dealtCards, ...dealt],
    },
  };
}

/**
 * Deal 3 cards to each player in clockwise order.
 * Cards are dealt one at a time per player for 3 rounds.
 *
 * @param deck - The current deck state
 * @param playerCount - Number of players (3–6)
 * @returns playerHands[playerIndex] = [card1, card2, card3], plus updated deck
 */
export function dealToPlayers(
  deck: Deck,
  playerCount: number
): { playerHands: Card[][]; deck: Deck } {
  if (playerCount < 3 || playerCount > 6) {
    throw new Error(`Invalid player count: ${playerCount}. Must be 3–6.`);
  }
  const cardsNeeded = playerCount * 3;
  if (deck.cards.length < cardsNeeded) {
    throw new Error(
      `Not enough cards. Need ${cardsNeeded}, have ${deck.cards.length}.`
    );
  }

  const playerHands: Card[][] = Array.from({ length: playerCount }, () => []);
  let currentDeck = deck;

  // Deal one card at a time to each player, 3 rounds
  for (let round = 0; round < 3; round++) {
    for (let i = 0; i < playerCount; i++) {
      const result = dealCard(currentDeck);
      playerHands[i].push(result.card);
      currentDeck = result.deck;
    }
  }

  return { playerHands, deck: currentDeck };
}

/**
 * Verify deck integrity: no duplicates, union of cards + dealtCards = 52.
 */
export function verifyDeckIntegrity(deck: Deck): boolean {
  const allCards = [...deck.cards, ...deck.dealtCards];
  if (allCards.length !== 52) return false;

  const seen = new Set<string>();
  for (const card of allCards) {
    const key = `${card.rank}-${card.suit}`;
    if (seen.has(key)) return false;
    seen.add(key);
  }
  return true;
}
